import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Navbar from './Navbar'
import useCompanyInfo from '../hooks/useCompanyInfo'
import { Wrapper, Card, Content } from './AppointmentPage'
import { CircularProgress, Typography, Button, Snackbar } from '@mui/material'
import { useTranslation } from 'react-i18next'
import useQuery from '../hooks/useQuery'
import { allowMockApi, hiveApiUrl } from '../firebase'
import moment from 'moment-timezone'
import ModalCancelAppointment from './ModalCancelAppointment'
import { useHistory } from 'react-router'
import LineSplit from './LineSplit'
import ErrorPage from './ErrorPage'

const SpinnerWrapper = styled.div`
  width: 100%;
  margin-top: 3rem;
  display: flex;
  justify-content: center;
`

function AppointmentDetailsPage() {
  let query = useQuery()
  const { t, i18n } = useTranslation()
  const reqParams = {
    company: query.get('company'),
    resource: query.get('resource'),
    booking: query.get('booking'),
  }

  const [dataFetchError, setDataFetchError] = useState('')

  if (!reqParams.company || !reqParams.booking || !reqParams.resource)
    return <ErrorPage error={t('missingUrlParams')} />

  const history = useHistory()

  const { company, loading } = useCompanyInfo(reqParams)

  const [appointmentData, setAppointmentData] = useState(null)

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const url = allowMockApi
      ? `/mock/appointment/${reqParams.company}/${reqParams.booking}`
      : `${hiveApiUrl}/api/appointment/details?company=${reqParams.company}&booking=${reqParams.booking}`

    !appointmentData &&
      !dataFetchError &&
      fetch(url).then((res) =>
        res.ok
          ? res.json().then((json) => setAppointmentData(json.data))
          : setDataFetchError(() => {
              const { error, message } = JSON.parse(res._bodyInit)
              return `${error}: ${message}`
            }),
      )
  }, [company, appointmentData, query, dataFetchError])

  const [openModal, setOpenModal] = useState(false)

  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const cancelAppointment = () => {
    const url = allowMockApi
      ? `/mock/cancel/${reqParams.company}`
      : `${hiveApiUrl}/api/appointment/cancel?company=${reqParams.company}`

    const body = {
      data: {
        bookingId: reqParams.booking,
      },
    }

    setCancelling(true)

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) =>
      res.ok
        ? res.json().then((json) => {
            setSubmitSuccess(true)
            setSubmitSuccessMessage(json.message)
            setCancelling(false)
            setOpenModal(false)
          })
        : setSubmitError(() => {
            const { error, message } = JSON.parse(res._bodyInit)
            return `${error}: ${message}`
          }),
    )
  }

  const ErrorAndLoading = () =>
    !dataFetchError ? (
      <SpinnerWrapper>
        <CircularProgress />
      </SpinnerWrapper>
    ) : (
      <ErrorPage error={dataFetchError} />
    )

  return !loading && company && appointmentData ? (
    <>
      <Navbar company={company} />
      <Wrapper>
        <Typography variant="h5" component="h5" style={{ padding: '1rem' }}>
          {t('yourAppointment')} {appointmentData.resource.displayName[i18n.language]}
        </Typography>
        <Card>
          <Content>
            <Typography variant="subtitle1" component="span" style={{ fontFamily: 'Lato-Bold' }}>
              {appointmentData.appointmentType.displayName[i18n.language]}
            </Typography>
            <Typography variant="body2" component="p" style={{ marginBottom: '1rem' }}>
              {appointmentData.appointmentType.description[i18n.language]}
            </Typography>
            <Typography variant="subtitle1" component="span" style={{ fontFamily: 'Lato-Bold' }}>
              {t('appointmentTime')}
            </Typography>
            <Typography variant="body2" component="span">
              {t('start')}:{' '}
              {moment(appointmentData.startTime).tz(appointmentData.tzRegion).format('lll')}
            </Typography>
            <Typography variant="body2" component="span" style={{ marginBottom: '1rem' }}>
              {t('end')}:{' '}
              {moment(appointmentData.endTime).tz(appointmentData.tzRegion).format('lll')}
            </Typography>
            {appointmentData.resource.address && (
              <>
                <Typography
                  variant="subtitle1"
                  component="span"
                  style={{ fontFamily: 'Lato-Bold' }}
                >
                  {t('address')}
                </Typography>
                {appointmentData.resource.address.split('\\n').map((line, index) => (
                  <Typography variant="body2" component="p" key={index}>
                    {line}
                  </Typography>
                ))}
              </>
            )}
            <LineSplit
              string={appointmentData.resource.description[i18n.language]}
              separator="<br/>"
            />
          </Content>
          <Button
            variant="outlined"
            color="error"
            style={{ margin: '1rem' }}
            onClick={() => setOpenModal(true)}
          >
            {t('cancelAppointment')}
          </Button>
          {submitError !== '' && (
            <Typography variant="body2" color="error" component="p" style={{ marginTop: '0.5rem' }}>
              Please try again - {submitError}
            </Typography>
          )}
          <Snackbar
            open={submitSuccess}
            autoHideDuration={3000}
            onClose={() =>
              history.push(`/?company=${reqParams.company}&resource=${reqParams.resource}`)
            }
            message={submitSuccessMessage}
          />
        </Card>
      </Wrapper>
      <ModalCancelAppointment
        openModal={openModal}
        setOpenModal={setOpenModal}
        cancelAppointment={cancelAppointment}
        cancelling={cancelling}
      />
    </>
  ) : (
    <ErrorAndLoading />
  )
}

export default AppointmentDetailsPage
